import React, { FC, useState, useRef, useEffect } from "react";
import {
    StyleSheet,
    Dimensions,
    View,
    Text,
    TouchableOpacity,
    SafeAreaView,
    Platform,
} from "react-native";
import { Camera, CameraType, FlashMode, VideoStabilization } from "expo-camera";
import { Video, Audio } from "expo-av";
import { Box, Icon, IconButton } from "native-base";
import { MaterialIcons, Ionicons, MaterialCommunityIcons, FontAwesome5 } from "@expo/vector-icons";
import { PinchGestureHandler } from 'react-native-gesture-handler';
import { useKeepAwake } from 'expo-keep-awake'; // NUEVO: Pantalla siempre encendida
import * as Brightness from 'expo-brightness'; // NUEVO: Ahorro de batería
import { formatDateLocal } from "../../utils/general";

const WINDOW_HEIGHT = Dimensions.get("window").height;

const closeButtonSize = Math.floor(WINDOW_HEIGHT * 0.032);
const captureSize = Math.floor(WINDOW_HEIGHT * 0.09);


export interface ICameraUIProps {
    onCallback: Function, onRequestClose: Function, forVideo?: boolean, bodyCam?: boolean
}


export const CameraUI: FC<ICameraUIProps> = (({ ...props }) => {
    const [hasPermission, setHasPermission] = useState(null);
    const [cameraType, setCameraType] = useState(CameraType.back);
    const [flashMode, setFlashMode] = useState(FlashMode.off);
    const [zoom, setZoom] = useState(0);

    const [isPreview, setIsPreview] = useState(false);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [isVideoRecording, setIsVideoRecording] = useState(false);
    const [videoSource, setVideoSource] = useState(null);
    const [elapsed, setElapsed] = React.useState<number>(0);
    const [started, setStarted] = React.useState<number>(0);
    const [videoQuality, setVideoQuality] = useState<'480p' | '720p'>('480p');
    const [isTakingPicture, setIsTakingPicture] = useState(false);
    const [isStartingRecording, setIsStartingRecording] = useState(false);
    const [isStoppingRecording, setIsStoppingRecording] = useState(false);
    const cameraRef = useRef();
    const bodyCam = props.bodyCam || false;
    const stopRequest = useRef(false);
    let _recordingTimer = null;

    const fragmentDuration = bodyCam ? 150 : 60;

    const qualityProfiles = {
        '480p': {
            label: 'SD',
            bitrate: 2 * 1000 * 1000,
            expoQuality: Camera.Constants.VideoQuality["480p"],
            color: "green.500" // Color verde para modo seguro
        },
        '720p': {
            label: 'HD',
            bitrate: 6 * 1000 * 1000,
            expoQuality: Camera.Constants.VideoQuality["720p"],
            color: "red.500"
        }
    };


    useKeepAwake();
    const playBeep = async () => {
        try {
            const { sound } = await Audio.Sound.createAsync(require('../../../assets/beep.mp3'));
            await sound.playAsync();
            // Liberar memoria tras sonar
            sound.setOnPlaybackStatusUpdate((status) => {
                sound.unloadAsync();
            });
        } catch (e) { console.log("Error sonido", e); }
    };

    useEffect(() => {
        (async () => {
            try {
                const { status } = await Camera.getCameraPermissionsAsync();
                console.log(status)
                if (status !== "granted")
                    await Camera.requestCameraPermissionsAsync();
                console.log("Necesito CAMARA")
                setIsCameraReady(true);
                if (props.forVideo) await Camera.getMicrophonePermissionsAsync();
                setHasPermission(status === "granted");
            } catch (e) {
                console.log(e);
                if (Platform.OS == "web") {
                    await Camera.requestCameraPermissionsAsync();
                    setHasPermission(true);
                }

            }
        })();
    }, []);

    const onCameraReady = () => {
        setIsCameraReady(true);
    };

    const takePicture = async () => {
        if (isTakingPicture) return;
        if (cameraRef.current) {
            setIsTakingPicture(true);
            try {
                const options = {
                    quality: 0.7, base64: true, skipProcessing: false, orientation: "auto",
                    width: 1800
                };
                //@ts-ignore
                const data = await cameraRef.current.takePictureAsync(options);
                const source = data.uri;
                if (source) {
                    props.onRequestClose();
                    props.onCallback(data);
                }
            } catch (e) {
                console.warn(e);
            } finally {
                setIsTakingPicture(false);
            }
        }
    };
    const changeZoom = (event) => {
        if (event.nativeEvent.scale > 1 && zoom < 1) {
            setZoom(zoom + 0.005);
        }
        if (event.nativeEvent.scale < 1 && zoom > 0) {
            setZoom(zoom - 0.005);
        }
    };

    const recordVideo = async () => {
        if (!cameraRef.current || isStartingRecording || isVideoRecording) return;
        setIsStartingRecording(true);

        setIsVideoRecording(true);
        stopRequest.current = false;
        setStarted(new Date().getTime());

        // Si es BodyCam, bajamos el brillo al iniciar
        if (bodyCam) {
            await Brightness.setBrightnessAsync(0.1);
        }

        setIsStartingRecording(false);
        await runRecordingLoop();
    };
    const runRecordingLoop = async () => {
        if (stopRequest.current || !cameraRef.current) return;

        try {
            const options = {
                quality: qualityProfiles[videoQuality].expoQuality,
                maxDuration: fragmentDuration,
                videoBitrate: qualityProfiles[videoQuality].bitrate,
                VideoStabilization: VideoStabilization.off
            };
            const fechaActual = formatDateLocal(new Date().getTime());
            const data = await cameraRef.current.recordAsync(options);
            data.name = fechaActual.concat(".mp4");
            if (bodyCam && !stopRequest.current) {
                console.log("Reiniciando fragmento automáticamente...");
                runRecordingLoop();
                props.onCallback(data);
            } else {
                props.onCallback(data);
                terminateSession();
            }
        } catch (error) {
            console.warn(error);
            terminateSession();
        }
    };

    const terminateSession = async () => {
        setIsVideoRecording(false);
        setIsStoppingRecording(false);
        if (bodyCam) {
            await Brightness.restoreSystemBrightnessAsync();
        }
        props.onRequestClose();
    };



    React.useEffect(() => {
        if (isVideoRecording && _recordingTimer == null) {
            _recordingTimer = setInterval(() => {
                setElapsed(((new Date().getTime() - started) / 1000));
            }, 1000);
        }
    }, [isVideoRecording]);

    const stopVideoRecording = () => {
        if (isStoppingRecording || stopRequest.current) return;
        setIsStoppingRecording(true);
        console.log("stopRecording")
        stopRequest.current = true;
        if (cameraRef.current) {
            // @ts-ignore
            cameraRef.current.stopRecording();
        }
    };

    const switchCamera = () => {
        if (isPreview) {
            return;
        }
        setCameraType((prevCameraType) =>
            prevCameraType === CameraType.back
                ? CameraType.front
                : CameraType.back
        );
    };

    const cancelPreview = async () => {
        // @ts-ignore
        await cameraRef.current.resumePreview();
        setIsPreview(false);
        setVideoSource(null);

    };

    const renderCancelPreviewButton = () => (
        <TouchableOpacity onPress={cancelPreview} style={styles.closeButton}>
            <View style={[styles.closeCross, { transform: [{ rotate: "45deg" }] }]} />
            <View
                style={[styles.closeCross, { transform: [{ rotate: "-45deg" }] }]}
            />
        </TouchableOpacity>
    );

    const renderVideoPlayer = () => (
        <Video
            source={{ uri: videoSource }}
            shouldPlay={true}
            style={styles.media}
        />
    );

    const renderVideoRecordIndicator = () => (
        <View style={styles.recordIndicatorContainer}>
            <Icon as={MaterialCommunityIcons} name="record-rec"
                size={"lg"} color="red.400" />
            <Text style={styles.recordTitle}>{`Grabando ${elapsed.toFixed(0)}'`}</Text>
        </View>
    );

    const renderCaptureControl = () => (
        <View style={styles.control}>

            <IconButton display={props.forVideo ? "none" : "flex"}
                onPress={() => { setFlashMode(flashMode === FlashMode.off ? FlashMode.on : FlashMode.off); }}
                icon={<Icon as={Ionicons} name={flashMode === FlashMode.off ? "md-flash-off" : 'md-flash'}
                    size={"xl"} color="white" />}
            />
            <IconButton display={!props.forVideo ? "none" : "flex"}
                onPress={() => { setFlashMode(flashMode === FlashMode.off ? FlashMode.torch : FlashMode.off); }}
                icon={<Icon as={Ionicons} name="md-flashlight-outline"
                    size={"xl"} color={flashMode === FlashMode.off ? "white" : 'yellow.400'} />}
            />
            <IconButton display={props.forVideo ? "none" : "flex"}
                disabled={isTakingPicture}
                onPress={takePicture}
                icon={<Icon as={MaterialIcons} name="camera"
                    size="6xl" color={isTakingPicture ? "gray.400" : "white"} />}
            />
            <IconButton display={props.forVideo && !isVideoRecording ? "flex" : "none"}
                disabled={isStartingRecording}
                onPress={recordVideo}
                icon={<Icon as={Ionicons} name="radio-button-on-sharp"
                    size="6xl" color={isStartingRecording ? "gray.400" : "red.400"} />}
            />
            <IconButton display={props.forVideo && isVideoRecording ? "flex" : "none"}
                disabled={isStoppingRecording}
                onPress={stopVideoRecording}
                icon={<Icon as={Ionicons} name="stop-circle-outline"
                    size="6xl" color={isStoppingRecording ? "gray.400" : "red.400"} />}
            />
            <IconButton
                disabled={isVideoRecording}
                onPress={switchCamera}
                icon={<Icon as={Ionicons} name="camera-reverse"
                    size={"xl"} color="white" />}
            />

        </View>
    );

    



    return (
        <>
            <PinchGestureHandler onGestureEvent={(event) => changeZoom(event)}>

                <Camera
                    ref={cameraRef}
                    style={styles.container}
                    type={cameraType}
                    useCamera2Api={false}
                    ratio="16:9"
                    zoom={zoom}
                    flashMode={flashMode}
                    onCameraReady={onCameraReady}
                    onMountError={(error) => {
                        console.log("camera error", error);
                    }}
                /></PinchGestureHandler>
            <IconButton style={styles.closeButton} size="lg" variant="solid" borderRadius={"full"} m="0"
                colorScheme="black"
                onPress={() => setVideoQuality(videoQuality === '480p' ? '720p' : '480p')}
                icon={<Icon
                    as={MaterialCommunityIcons}
                    name={videoQuality === '720p' ? "high-definition" : "standard-definition"}
                    size={"xl"}
                    color={isVideoRecording ? "gray.500" : qualityProfiles[videoQuality].color}
                />} />
            <View style={styles.container}>
                {isVideoRecording && renderVideoRecordIndicator()}
                {videoSource && renderVideoPlayer()}
                {isPreview && renderCancelPreviewButton()}
                {!videoSource && !isPreview && renderCaptureControl()}
            </View>
        </>
    );
});

const styles = StyleSheet.create({
    container: {
        ...StyleSheet.absoluteFillObject,
    },
    closeButton: {
        position: "absolute",
        top: 35,
        right: 15,
        height: closeButtonSize,
        width: closeButtonSize,
        borderRadius: Math.floor(closeButtonSize / 2),
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#FFFFFF",
        opacity: 0.7,
        zIndex: 2,
    },
    media: {
        ...StyleSheet.absoluteFillObject,
    },
    closeCross: {
        width: "68%",
        height: 1,
        backgroundColor: "black",
    },
    control: {
        position: "absolute",
        flexDirection: "row",
        bottom: 10,
        width: "100%",
        alignItems: "center",
        justifyContent: "space-around",
    },
    capture: {
        backgroundColor: "#f5f6f5",
        borderRadius: 5,
        height: captureSize,
        width: captureSize,
        marginHorizontal: 31,
    },
    recordIndicatorContainer: {
        flexDirection: "row",
        position: "absolute",
        top: 25,
        alignSelf: "center",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "transparent",
        opacity: 0.7,
    },
    recordTitle: {
        fontSize: 14,
        color: "#ffffff",
        textAlign: "center",
    },
    recordDot: {
        borderRadius: 3,
        height: 6,
        width: 6,
        backgroundColor: "#ff0000",
        marginHorizontal: 5,
    },
    text: {
        color: "#fff",
    },
});